"use client";

// Lightweight attachment preview (plan 043 P9, gap-audit C11/T6): the
// block's default for clicking a staged or sent attachment. Images and PDFs
// open in a dialog; anything else gets an "Open in new tab" link. This is
// deliberately small — no Office/markdown/code viewers, no processing
// status — so it stays registry-safe; the flagship keeps its richer private
// `FilePreviewModal` by passing its own `onAttachmentActivate` handlers.
import { c as _c } from "react/compiler-runtime";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@workspace/ui/components/dialog";
import { ExternalLinkIcon, Loader2Icon } from "lucide-react";
import { createContext, type ReactNode, useContext, useEffect, useState } from "react";

/**
 * What to preview. Exactly one of `file` (a staged local File — previewed
 * through an object URL, no network), `url`, or `resolveUrl` (a sent
 * document part that carries only its upload id) supplies the bytes.
 */
export interface AttachmentPreviewTarget {
  file?: File;
  mediaType?: string;
  name: string;
  resolveUrl?: () => Promise<string>;
  url?: string;
}

/** Image and PDF render inline; everything else is link-only. */
export function isPreviewableMediaType(mediaType: string | undefined): boolean {
  return Boolean(mediaType && (mediaType.startsWith("image/") || mediaType === "application/pdf"));
}
interface AttachmentPreviewContextValue {
  open: (target: AttachmentPreviewTarget) => void;
  /** The uploads source's URL lookup for sent document parts. */
  resolveUrl?: (uploadId: string) => Promise<string>;
}
const AttachmentPreviewContext = createContext<AttachmentPreviewContextValue | null>(null);

/** Provided by `MagnusChat`; null when a `Transcript` renders standalone, in
 *  which case sent tiles keep the plain open-in-a-new-tab behavior. */
export function useAttachmentPreview() {
  return useContext(AttachmentPreviewContext);
}
export function AttachmentPreviewProvider(t0) {
  const $ = _c(3);
  const {
    children,
    value
  } = t0;
  let t1;
  if ($[0] !== children || $[1] !== value) {
    t1 = <AttachmentPreviewContext.Provider value={value}>{children}</AttachmentPreviewContext.Provider>;
    $[0] = children;
    $[1] = value;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  return t1;
}
type Resolved = {
  state: "loading";
} | {
  state: "error";
} | {
  state: "ready";
  url: string;
};

/** Turn a target into a URL: an object URL for a local File (revoked when
 *  the target changes), the URL as given, or the async lookup. */
function useTargetUrl(target) {
  const $ = _c(3);
  const [resolved, setResolved] = useState(null);
  let t0;
  let t1;
  if ($[0] !== target) {
    t0 = () => {
      if (!target) {
        setResolved(null);
        return;
      }
      if (target.file) {
        const objectUrl = URL.createObjectURL(target.file);
        setResolved({
          state: "ready",
          url: objectUrl
        });
        return () => URL.revokeObjectURL(objectUrl);
      }
      if (target.url) {
        setResolved({
          state: "ready",
          url: target.url
        });
        return;
      }
      if (!target.resolveUrl) {
        setResolved({
          state: "error"
        });
        return;
      }
      let cancelled = false;
      setResolved({
        state: "loading"
      });
      target.resolveUrl().then(url => {
        if (!cancelled) {
          setResolved({
            state: "ready",
            url
          });
        }
      }, () => {
        if (!cancelled) {
          setResolved({
            state: "error"
          });
        }
      });
      return () => {
        cancelled = true;
      };
    };
    t1 = [target];
    $[0] = target;
    $[1] = t0;
    $[2] = t1;
  } else {
    t0 = $[1];
    t1 = $[2];
  }
  useEffect(t0, t1);
  return resolved;
}
function PreviewBody(t0) {
  const $ = _c(7);
  const {
    target,
    url
  } = t0;
  if (target.mediaType?.startsWith("image/")) {
    let t1;
    if ($[0] !== target.name || $[1] !== url) {
      t1 = <img alt={target.name} className="mx-auto max-h-[70vh] max-w-full rounded-md object-contain" height={800} src={url} width={800} />;
      $[0] = target.name;
      $[1] = url;
      $[2] = t1;
    } else {
      t1 = $[2];
    }
    return t1;
  }
  if (target.mediaType === "application/pdf") {
    let t1;
    if ($[3] !== target.name || $[4] !== url) {
      t1 = <iframe className="h-[70vh] w-full rounded-md border" src={url} title={target.name} />;
      $[3] = target.name;
      $[4] = url;
      $[5] = t1;
    } else {
      t1 = $[5];
    }
    return t1;
  }
  let t1;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <p className="text-muted-foreground text-sm">There's no preview for this file type.</p>;
    $[6] = t1;
  } else {
    t1 = $[6];
  }
  return t1;
}

/**
 * The preview dialog. `target` null → closed. The "Open in new tab" link is
 * always offered once a URL exists, because an inline PDF can still be
 * blocked by the browser or the storage host's headers.
 */
export function AttachmentPreviewDialog(t0) {
  const $ = _c(25);
  const {
    onClose,
    target
  } = t0;
  const resolved = useTargetUrl(target);
  let t1;
  if ($[0] !== onClose) {
    t1 = open => {
      if (!open) {
        onClose();
      }
    };
    $[0] = onClose;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const t2 = target !== null;
  const t3 = target?.name ?? "Attachment";
  let t4;
  if ($[2] !== t3) {
    t4 = <DialogTitle className="truncate pr-6">{t3}</DialogTitle>;
    $[2] = t3;
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  let t5;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <DialogDescription className="sr-only">Attachment preview</DialogDescription>;
    $[4] = t5;
  } else {
    t5 = $[4];
  }
  let t6;
  if ($[5] !== t4) {
    t6 = <DialogHeader>{t4}{t5}</DialogHeader>;
    $[5] = t4;
    $[6] = t6;
  } else {
    t6 = $[6];
  }
  let t7;
  if ($[7] !== resolved?.state || $[8] !== target) {
    t7 = target && resolved?.state === "loading" && <div className="flex justify-center py-12"><Loader2Icon aria-label="Loading preview" className="size-5 animate-spin text-muted-foreground" /></div>;
    $[7] = resolved?.state;
    $[8] = target;
    $[9] = t7;
  } else {
    t7 = $[9];
  }
  let t8;
  if ($[10] !== resolved?.state || $[11] !== target) {
    t8 = target && resolved?.state === "error" && <p className="text-muted-foreground text-sm">Couldn't open this file. Please try again in a moment.</p>;
    $[10] = resolved?.state;
    $[11] = target;
    $[12] = t8;
  } else {
    t8 = $[12];
  }
  let t9;
  if ($[13] !== resolved || $[14] !== target) {
    t9 = target && resolved?.state === "ready" && <><PreviewBody target={target} url={resolved.url} /><a className="inline-flex items-center gap-1.5 self-start text-primary text-sm hover:underline" href={resolved.url} rel="noopener noreferrer" target="_blank"><ExternalLinkIcon aria-hidden={true} className="size-3.5" />Open in new tab</a></>;
    $[13] = resolved;
    $[14] = target;
    $[15] = t9;
  } else {
    t9 = $[15];
  }
  let t10;
  if ($[16] !== t6 || $[17] !== t7 || $[18] !== t8 || $[19] !== t9) {
    t10 = <DialogContent className="sm:max-w-3xl">{t6}{t7}{t8}{t9}</DialogContent>;
    $[16] = t6;
    $[17] = t7;
    $[18] = t8;
    $[19] = t9;
    $[20] = t10;
  } else {
    t10 = $[20];
  }
  let t11;
  if ($[21] !== t1 || $[22] !== t10 || $[23] !== t2) {
    t11 = <Dialog onOpenChange={t1} open={t2}>{t10}</Dialog>;
    $[21] = t1;
    $[22] = t10;
    $[23] = t2;
    $[24] = t11;
  } else {
    t11 = $[24];
  }
  return t11;
}